import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export const SUPPORTED_CURRENCIES = [
  { code: 'EUR', nameKey: 'cur.eur' },
  { code: 'USD', nameKey: 'cur.usd' },
  { code: 'GBP', nameKey: 'cur.gbp' },
  { code: 'CVE', nameKey: 'cur.cve' },
  { code: 'STN', nameKey: 'cur.stn' },
];
export const CURRENCY_CODES = SUPPORTED_CURRENCIES.map((c) => c.code);

const STORAGE_KEY = 'eco-currency';
const REGION_CURRENCY = { US: 'USD', GB: 'GBP', CV: 'CVE', ST: 'STN' };

/** Suggest a currency from the visitor locale; EUR is always the fallback. */
export const detectLocaleCurrency = (locale) => {
  const tag = String(locale || '').toUpperCase();
  const region = tag.split('-')[1] || '';
  return REGION_CURRENCY[region] || 'EUR';
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && CURRENCY_CODES.includes(saved)) return saved;
    return detectLocaleCurrency(typeof navigator !== 'undefined' ? navigator.language : '');
  });
  const [ratesDoc, setRatesDoc] = useState(null); // {rates, source, stale, last_update, attribution}
  const [ratesFailed, setRatesFailed] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fx/rates`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('fx unavailable'))))
      .then((doc) => setRatesDoc(doc))
      .catch(() => setRatesFailed(true));
  }, []);

  const setCurrency = useCallback((code) => {
    if (!CURRENCY_CODES.includes(code)) return;
    localStorage.setItem(STORAGE_KEY, code);
    setCurrencyState(code);
  }, []);

  /**
   * Convert an approved public EUR amount into the selected currency.
   * Returns { amount, currency, estimate } or null when no reliable rate exists —
   * callers must then show the original EUR price, never an invented value.
   */
  const convert = useCallback((amountEur) => {
    const value = Number(amountEur);
    if (!Number.isFinite(value)) return null;
    if (currency === 'EUR') return { amount: value, currency: 'EUR', estimate: false };
    const rate = ratesDoc?.rates?.[currency];
    if (!rate) return null;
    return { amount: value * Number(rate), currency, estimate: true };
  }, [currency, ratesDoc]);

  const formatMoney = useCallback((amountEur, lang = 'pt') => {
    const converted = convert(amountEur);
    if (!converted) {
      return { text: new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(amountEur)), estimate: false, fellBack: currency !== 'EUR' };
    }
    const digits = converted.currency === 'CVE' || converted.currency === 'STN' ? 0 : 2;
    return { text: new Intl.NumberFormat(lang, { style: 'currency', currency: converted.currency, maximumFractionDigits: digits }).format(converted.amount), estimate: converted.estimate, fellBack: false };
  }, [convert, currency]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    currencies: SUPPORTED_CURRENCIES,
    convert,
    formatMoney,
    ratesMeta: ratesDoc ? { source: ratesDoc.source, stale: !!ratesDoc.stale, lastUpdate: ratesDoc.last_update, attribution: ratesDoc.attribution } : null,
    ratesFailed,
  }), [currency, setCurrency, convert, formatMoney, ratesDoc, ratesFailed]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext) || {
  currency: 'EUR', setCurrency: () => {}, currencies: SUPPORTED_CURRENCIES,
  convert: (v) => ({ amount: Number(v), currency: 'EUR', estimate: false }),
  formatMoney: (v, lang = 'pt') => ({ text: new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(v)), estimate: false, fellBack: false }),
  ratesMeta: null, ratesFailed: false,
};
